import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 * MetroSync modification: menu opens on CLICK instead of hover (v3 mockup behavior).
 * - `toggle` handler wired via on:click in snippets/header-menu.liquid, bound to the
 *   PARENT LINK ONLY (never the li) so clicks inside the dropdown panel never reach it
 * - click outside the menu closes it
 * - parent items with submenus no longer navigate on click
 *
 * IMPORTANT: the theme's on:click delegation retargets the event so inside `toggle`
 * event.target is the BOUND element, never the descendant that was actually clicked,
 * and the handler may run after a microtask boundary. We record the true click target
 * with a window-level capture listener (fires before the delegation), first-one-wins
 * per click burst, cleared on the NEXT TASK (setTimeout 0, NOT a microtask — the
 * delegation defers past microtasks, which would see the target already cleared).
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {Element | null} true target of the in-flight click (pre-delegation) */
  #realClickTarget = null;

  #realClickArmed = false;

  connectedCallback() {
    super.connectedCallback();

    // MetroSync: capture the REAL click target before the on:click delegation
    // retargets to the bound element. First capture in a burst wins; cleared on
    // the next macrotask so it survives the delegation's microtask deferral.
    window.addEventListener(
      'click',
      (event) => {
        if (this.#realClickArmed) return;
        this.#realClickArmed = true;
        this.#realClickTarget = event.target instanceof Element ? event.target : null;
        setTimeout(() => {
          this.#realClickArmed = false;
          this.#realClickTarget = null;
        }, 0);
      },
      { capture: true, signal: this.#abortController.signal }
    );

    // MetroSync: close the menu when clicking anywhere outside of it
    document.addEventListener(
      'click',
      (event) => {
        if (this.#state.activeItem && event.target instanceof Node && !this.contains(event.target)) {
          this.#forceDeactivate();
        }
      },
      { signal: this.#abortController.signal }
    );

    // MetroSync: close on Escape
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#state.activeItem) {
          this.#forceDeactivate();
        }
      },
      { signal: this.#abortController.signal }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * MetroSync: toggle the clicked menu item open/closed.
   * Only the TOP-LEVEL parent link (or the "More" button) toggles; a click that
   * really landed inside the dropdown panel navigates normally.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    // Resolve where the click REALLY landed (delegation retargets to the bound element).
    const real = this.#realClickTarget instanceof Element ? this.#realClickTarget : event.target;

    // Clicks inside the open dropdown are navigation, never a toggle.
    if (real.closest('.menu-list__submenu')) return;

    const li = real.closest('li.menu-list__list-item') ?? real.closest('li') ?? event.target.closest('li');
    const item = findMenuItem(li ?? real);
    if (!item) return;

    const hasSubmenu = item.getAttribute('aria-haspopup') === 'true' || (li ? li.matches('[slot="more"]') : false);

    if (!hasSubmenu) return; // leaf link: allow normal navigation

    event.preventDefault();

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    if (item === this.#state.activeItem) {
      this.#forceDeactivate(item);
    } else {
      this.#activateHandler(event);
    }
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target.closest('li') ?? event.target);

    if (!item || item == this.#state.activeItem) return;

    const targetSlotSource = event.target.closest('li') ?? event.target;
    const isDefaultSlot = targetSlotSource instanceof HTMLElement ? targetSlotSource.slot === '' : true;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately, regardless of hover state.
   * @param {HTMLElement | null} [item]
   */
  #forceDeactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item unless the overflow menu is hovered
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (this.overflowHovered) return;
    this.#forceDeactivate(item);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
